/**
 * @fileoverview AgedPayablesChart - Stacked bar chart with aging buckets
 *
 * Purpose: Display payables by aging (0-30, 31-60, 61-90, 90+ days).
 * Features polished styling: rounded top bars, subtle grid, clean axes.
 *
 * Used by: Review phase payables aging analysis
 */

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/ui/composed/data-display/chart";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  periodAlignedLayout,
  type PeriodAlignedChartProps,
} from "./periodAlignedLayout";

const config = {
  bucket0_30: { label: "0-30 days", color: "hsl(var(--chart-1))" },
  bucket31_60: { label: "31-60 days", color: "hsl(var(--chart-2))" },
  bucket61_90: { label: "61-90 days", color: "hsl(var(--chart-5))" },
  bucket90plus: { label: "90+ days", color: "hsl(var(--chart-4))" },
} satisfies ChartConfig;

const dollarFormatter = (v: number) => `$${(v / 1000).toFixed(0)}k`;

export interface AgedPayablesChartProps extends PeriodAlignedChartProps {
  periods: string[];
  bucket0_30: (number | null)[];
  bucket31_60: (number | null)[];
  bucket61_90: (number | null)[];
  bucket90plus: (number | null)[];
}

/** Stacked bars of payables per period by aging bucket; nulls leave gaps. */
export function AgedPayablesChart({
  periods,
  bucket0_30,
  bucket31_60,
  bucket61_90,
  bucket90plus,
  plotInsetLeft,
}: AgedPayablesChartProps) {
  const layout = periodAlignedLayout(plotInsetLeft, 60);
  // Missing values stay null — a bar is skipped and a line breaks — rather
  // than plotting as zero beside a table that shows a dash (MOR-1282 QA).
  const data = periods.map((period, i) => ({
    period,
    bucket0_30: bucket0_30[i] ?? null,
    bucket31_60: bucket31_60[i] ?? null,
    bucket61_90: bucket61_90[i] ?? null,
    bucket90plus: bucket90plus[i] ?? null,
  }));

  return (
    <ChartContainer config={config} className="min-h-[180px] w-full">
      <BarChart data={data} margin={layout.margin}>
        <CartesianGrid
          vertical={false}
          stroke="hsl(var(--border))"
          strokeOpacity={0.5}
        />
        <XAxis
          dataKey="period"
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tickFormatter={dollarFormatter}
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          tickLine={false}
          axisLine={false}
          width={layout.yAxisWidth}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              formatter={(v) =>
                typeof v === "number" ? dollarFormatter(v) : "—"
              }
            />
          }
        />
        <ChartLegend content={<ChartLegendContent />} />
        <Bar dataKey="bucket0_30" stackId="a" fill="hsl(var(--chart-1))" />
        <Bar dataKey="bucket31_60" stackId="a" fill="hsl(var(--chart-2))" />
        <Bar dataKey="bucket61_90" stackId="a" fill="hsl(var(--chart-5))" />
        <Bar
          dataKey="bucket90plus"
          stackId="a"
          fill="hsl(var(--chart-4))"
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ChartContainer>
  );
}
